(() => {
  function clcEmitContextChanged(productInfo) {
    if (!productInfo) return;
    productInfo.dispatchEvent(new CustomEvent('clc:context-changed', { bubbles: false }));
  }

  function clcFormatWithDelimiters(cents, precision, thousands, decimal) {
    const t = thousands ?? ',';
    const d = decimal ?? '.';
    if (Number.isNaN(cents) || cents == null) return '0';
    const num = (Number(cents) / 100).toFixed(precision);
    const parts = num.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${t}`);
    const centsPart = parts[1] ? d + parts[1] : '';
    return dollars + centsPart;
  }

  /** Same placeholder rules as Shopify's legacy formatMoney (shop.money_format). */
  function clcFormatMoneyWithTemplate(cents, formatString) {
    const fmt = String(formatString);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholderRegex);
    if (!match) return null;
    const key = match[1];
    let value = '';
    switch (key) {
      case 'amount':
        value = clcFormatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = clcFormatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = clcFormatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = clcFormatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = clcFormatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = clcFormatWithDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = clcFormatWithDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(placeholderRegex, value);
  }

  function clcFormatMoneyCents(cents, format) {
    let n = Math.round(Number(cents));

    const centsPart = n % 100;

    if (centsPart < 10) {
      n = n - centsPart;
    }
    if (Number.isNaN(n)) return '';
    const fmt = format && String(format).trim();
    if (fmt && /\{\{\s*\w+\s*\}\}/.test(fmt)) {
      const templated = clcFormatMoneyWithTemplate(n, fmt);
      if (templated != null) return templated;
    }
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function' && fmt) {
      return window.Shopify.formatMoney(n, fmt);
    }
    const code =
      (typeof window.Shopify !== 'undefined' && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
      }).format(n / 100);
    } catch {
      return (n / 100).toFixed(2);
    }
  }

  function clcNormalizeSellingPlanId(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
    const s = String(raw);
    const tail = s.match(/(\d+)\s*$/);
    if (tail) return parseInt(tail[1], 10);
    const n = parseInt(s, 10);
    return Number.isNaN(n) ? null : n;
  }

  function clcParseSubscriptionPricing(sectionId) {
    if (!sectionId) return null;
    const el = document.getElementById(`ClcSubscriptionPricing-${sectionId}`);
    if (!el || !el.textContent.trim()) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function clcPlanIdForVariant(pricing, variantId, allocationIndex) {
    if (!pricing?.variants || variantId == null || variantId === '') return null;
    const row = pricing.variants[String(variantId)];
    if (!row) return null;
    const idx =
      typeof allocationIndex === 'number' && !Number.isNaN(allocationIndex) ? allocationIndex : 0;
    const alloc = Array.isArray(row.allocations) ? row.allocations[idx] : null;
    if (!alloc) return null;
    return clcNormalizeSellingPlanId(alloc.planId);
  }

  function clcSyncFormSellingPlan(productInfo, pricing) {
    const form = productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    form.querySelectorAll('input[name="selling_plan"][data-clc-selling-plan]').forEach((el) => el.remove());

    if (!pricing?.variants) return;

    const idx = 0;
    const pi = productInfo;
    const flavorBtn = pi.querySelector('clc-custom-variants .clc-option-button.is-active[data-variant-id]');
    let activeVariantId = flavorBtn?.dataset.variantId;
    if (!activeVariantId) {
      activeVariantId = form.querySelector('input[name="id"]')?.value;
    }
    const qtyBtn = pi.querySelector('clc-custom-quantity-option .clc-option-button.is-active');
    const qtySource = qtyBtn?.closest('clc-custom-quantity-option');
    const varietyPackProductId = qtySource?.dataset?.varietyPackProductId;
    const flavorProductId = flavorBtn?.dataset.productId || pi.dataset.productId;
    const isVarietyBundle =
      varietyPackProductId &&
      flavorProductId &&
      String(flavorProductId) === String(varietyPackProductId) &&
      qtySource;

    if (isVarietyBundle) {
      return;
    }

    const planId = clcPlanIdForVariant(pricing, activeVariantId, idx);
    if (planId == null) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.setAttribute('data-clc-selling-plan', 'true');
    input.value = String(planId);
    form.appendChild(input);
  }

  function clcRcBundleRandomSegment() {
    return Math.random().toString(36).substring(2, 10);
  }

  function clcReadRcBundleConfig(productInfo) {
    const w =
      productInfo?.querySelector('clc-subscription-widget') ||
      productInfo?.querySelector('clc-consumption-buy-box');
    if (!w) return null;
    const vid = w.dataset.rcBundleVariantId;
    const pid = w.dataset.rcBundleProductId;
    const cid = w.dataset.rcBundleCollectionId;
    const parent = w.dataset.rcBundleParent;
    const variant = w.dataset.rcBundleVariant;
    if (!vid || !cid || parent == null || String(parent).trim() === '') return null;
    return {
      variantId: String(vid).trim(),
      productId: pid ? String(pid).trim() : '',
      collectionId: String(cid).trim(),
      parent: String(parent),
      variant: String(variant).trim(),
    };
  }

  function clcBuildRcBundleProperties(cfg) {
    if (!cfg) return null;
    const seg = clcRcBundleRandomSegment();
    return {
      _rc_bundle: `${seg}:${cfg.variantId}`,
      _rc_bundle_parent: cfg.parent,
      _rc_bundle_variant: cfg.variant,
      _rc_bundle_collection_id: cfg.collectionId,
    };
  }

  function clcParseSubscriptionDiscountPercent(el) {
    if (!el?.dataset) return 0;
    let raw = String(el.dataset.subscriptionDiscountPercent ?? '').trim();
    if (!raw) return 0;
    raw = raw.replace(',', '.');
    const n = parseFloat(raw);
    if (Number.isNaN(n)) return 0;
    return Math.min(100, Math.max(0, n));
  }

  function clcWaitForRechargeBundleApi(timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        const bundleApi = window.recharge?.bundle || window.recharge;
        if (bundleApi?.getDynamicBundleItems) {
          resolve(bundleApi);
          return;
        }
        if (Date.now() - started >= timeoutMs) {
          reject(new Error('Recharge bundle checkout is unavailable. Please refresh and try again.'));
          return;
        }
        window.setTimeout(check, 100);
      };
      check();
    });
  }

  async function clcBuildRechargeBundleItems(bundleItems, cfg, pricing) {
    const bundleApi = await clcWaitForRechargeBundleApi();
    if (!cfg?.productId || !cfg?.variantId || !cfg?.collectionId) {
      throw new Error('Recharge bundle settings are incomplete.');
    }
    if (bundleItems.some((item) => !item.productId || !item.id || !item.qty)) {
      throw new Error('One or more Recharge bundle products are incomplete.');
    }

    const selections = bundleItems.map((item) => {
      const selection = {
        collectionId: String(cfg.collectionId),
        externalProductId: String(item.productId),
        externalVariantId: String(item.id),
        quantity: Math.max(1, item.qty),
      };
      const planId = clcPlanIdForVariant(pricing, String(item.id), 0);
      if (planId != null) selection.sellingPlan = planId;
      return selection;
    });
    const bundle = {
      externalProductId: String(cfg.productId),
      externalVariantId: String(cfg.variantId),
      selections,
    };

    if (typeof bundleApi.validateDynamicBundle === 'function') {
      const validation = await bundleApi.validateDynamicBundle(bundle);
      if (validation !== true) {
        throw new Error(typeof validation === 'string' ? validation : 'Invalid bundle selection.');
      }
    }

    const items = await bundleApi.getDynamicBundleItems(bundle, cfg.parent);
    if (!Array.isArray(items) || !items.length) {
      throw new Error('Recharge did not return bundle cart items.');
    }
    return items;
  }

  function clcCartItemIsGifted(item) {
    return Boolean(item?.properties && item.properties._Gifted != null);
  }

  function clcCartIsGiftsOnly(cart) {
    const items = cart?.items || [];
    return items.length > 0 && items.every((item) => clcCartItemIsGifted(item));
  }

  // First-visit race: gift apps and pouch add can race on an empty cart token.
  // Touch cart.js first so a cart cookie exists before either write.
  async function clcEnsureCartSession() {
    const root = window.Shopify?.routes?.root || '/';
    try {
      await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
    } catch (_err) {
      /* ignore */
    }
  }

  function clcCollectPouchIds(intendedItems, addResponse) {
    const ids = [];
    (intendedItems || []).forEach((item) => {
      const id = item?.id ?? item?.variant_id;
      if (id != null && id !== '') ids.push(String(id));
    });
    const responseItems = Array.isArray(addResponse?.items)
      ? addResponse.items
      : addResponse?.variant_id != null || addResponse?.id != null
        ? [addResponse]
        : [];
    responseItems.forEach((item) => {
      if (item?.variant_id != null) ids.push(String(item.variant_id));
      if (item?.id != null) ids.push(String(item.id));
    });
    return [...new Set(ids.filter(Boolean))];
  }

  async function clcPublishCartUpdate(data, timeoutMs = 2500) {
    try {
      await Promise.race([
        publish(PUB_SUB_EVENTS.cartUpdate, data),
        new Promise((resolve) => window.setTimeout(resolve, timeoutMs)),
      ]);
    } catch (_err) {
      /* gift-app subscribers must not block checkout */
    }
  }

  // Checkout gate only: do not change how bundle lines are built.
  // A: wait until at least one intended pouch variant is in the cart.
  // B: never treat gifts-only (or empty variant ids) as ready for checkout.
  async function clcWaitForCheckoutReady(variantIds, attempts = 16) {
    const wanted = [...new Set(variantIds.map((id) => String(id || '')).filter(Boolean))];
    if (!wanted.length) return { ok: false, reason: 'missing-variant' };
    const root = window.Shopify?.routes?.root || '/';
    let lastCart = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (response.ok) {
          const cart = await response.json();
          lastCart = cart;
          const present = new Set((cart.items || []).map((item) => String(item.variant_id)));
          const hasPouch = wanted.some((id) => present.has(id));
          const giftsOnly = clcCartIsGiftsOnly(cart);
          if (hasPouch && !giftsOnly) return { ok: true, cart };
        }
      } catch (_err) {
        /* retry */
      }
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    return {
      ok: false,
      reason: clcCartIsGiftsOnly(lastCart) ? 'gifts-only' : 'pouches-missing',
      cart: lastCart,
    };
  }

  async function clcWaitForCartVariants(variantIds, attempts = 16) {
    const result = await clcWaitForCheckoutReady(variantIds, attempts);
    return result.ok;
  }

  async function clcPostCartAddJson(payload) {
    const addUrl = window.routes?.cart_add_url ? `${window.routes.cart_add_url}.js` : '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    let parsedState = null;
    try {
      parsedState = await response.json();
    } catch (_err) {
      return { ok: false, reason: 'add-failed', response, parsedState: null };
    }
    if (!response.ok || parsedState?.status) {
      return { ok: false, reason: 'add-failed', response, parsedState };
    }
    return { ok: true, response, parsedState };
  }

  // Add returns OK is not enough on first visit — re-read /cart.js and retry if pouches missing.
  async function clcAddPouchesAndVerify(payload, intendedItems, { retries = 2, waitAttempts = 12 } = {}) {
    let pouchIds = clcCollectPouchIds(intendedItems, null);
    let lastParsed = null;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      const posted = await clcPostCartAddJson(payload);
      if (!posted.ok) {
        if (attempt === retries) {
          return {
            ok: false,
            reason: 'add-failed',
            parsedState: posted.parsedState,
            pouchIds,
            message:
              posted.parsedState?.description ||
              posted.parsedState?.message ||
              'Unable to add the bundle. Please try again.',
          };
        }
        continue;
      }
      lastParsed = posted.parsedState;
      pouchIds = clcCollectPouchIds(intendedItems, lastParsed);
      const ready = await clcWaitForCheckoutReady(pouchIds, waitAttempts);
      if (ready.ok) {
        return { ok: true, cart: ready.cart, parsedState: lastParsed, pouchIds };
      }
    }
    const finalReady = await clcWaitForCheckoutReady(pouchIds, 1);
    return {
      ok: false,
      reason: finalReady.reason || 'pouches-missing',
      parsedState: lastParsed,
      pouchIds,
      cart: finalReady.cart,
    };
  }

  function clcEnsureFormRcBundleOnSubmit(productInfo) {
    const form = productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || form.dataset.clcRcBundleSubmitBound === 'true') return;
    form.dataset.clcRcBundleSubmitBound = 'true';
    form.addEventListener(
      'submit',
      () => {
        form.querySelectorAll('input[data-clc-rc-bundle-prop]').forEach((el) => el.remove());
        const cfg = clcReadRcBundleConfig(productInfo);
        if (!cfg) return;
        const props = clcBuildRcBundleProperties(cfg);
        if (!props) return;
        Object.entries(props).forEach(([key, val]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = String(val);
          input.setAttribute('data-clc-rc-bundle-prop', 'true');
          form.appendChild(input);
        });
      },
      true
    );
  }

  class ClcCustomVariants extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.onDocPointerDown = (e) => {
        const dropdown = this.querySelector('.clc-variant-dropdown');
        if (!dropdown || !dropdown.classList.contains('is-open')) return;
        if (dropdown.contains(e.target)) return;
        this.closeDropdown();
      };
      document.addEventListener('pointerdown', this.onDocPointerDown, true);
      this.initializeSelection();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('pointerdown', this.onDocPointerDown, true);
    }

    initializeSelection() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;
      let selectedButton =
        this.querySelector('.clc-option-button.is-active[data-variant-id]:not([data-sold-out="true"])') ||
        this.querySelector('.clc-option-button[data-variant-id]:not([data-sold-out="true"])');
      if (!selectedButton) return;
      this.setActiveButton(selectedButton);
      this.updateAddToCartVariant(productInfo, selectedButton.dataset.variantId);
      this.updateGalleryImage(productInfo, selectedButton.dataset.imageUrl);
      if (selectedButton.dataset.productId) {
        productInfo.dataset.productId = selectedButton.dataset.productId;
      }
      this.syncAddToCartState(productInfo);
      clcEmitContextChanged(productInfo);
    }

    syncAddToCartState(productInfo) {
      if (!productInfo) return;
      const activeButton = this.querySelector('.clc-option-button.is-active[data-variant-id]');
      const currentProductId = this.dataset.currentProductId;
      const activeIsCurrentProduct =
        activeButton &&
        currentProductId &&
        String(activeButton.dataset.productId) === String(currentProductId);
      const isSoldOut =
        activeButton?.dataset.soldOut === 'true' ||
        (activeIsCurrentProduct && this.dataset.currentProductSoldOut === 'true');

      const form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
      const submitBtn = form?.querySelector('.product-form__submit, button[name="add"]');
      if (!submitBtn) return;

      submitBtn.disabled = Boolean(isSoldOut);
      submitBtn.classList.toggle('is-sold-out-disabled', Boolean(isSoldOut));
      if (isSoldOut) {
        submitBtn.setAttribute('aria-disabled', 'true');
      } else {
        submitBtn.removeAttribute('aria-disabled');
      }
    }

    closeDropdown() {
      const dropdown = this.querySelector('.clc-variant-dropdown');
      dropdown?.classList.remove('is-open');
      const panel = this.querySelector('.clc-variant-dropdown__panel');
      const trigger = this.querySelector('.clc-variant-dropdown__trigger');
      panel?.setAttribute('hidden', '');
      trigger?.setAttribute('aria-expanded', 'false');
    }

    toggleDropdown() {
      const panel = this.querySelector('.clc-variant-dropdown__panel');
      const trigger = this.querySelector('.clc-variant-dropdown__trigger');
      const dropdown = this.querySelector('.clc-variant-dropdown');
      if (!panel || !trigger) return;
      const willOpen = panel.hasAttribute('hidden');
      if (willOpen) {
        panel.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        dropdown?.classList.add('is-open');
      } else {
        this.closeDropdown();
      }
    }

    updateAddToCartVariant(productInfo, variantId) {
      if (!variantId) return;
      const form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;
      const variantInput = form.querySelector('input[name="id"]');
      if (!variantInput) return;
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateGalleryImage(productInfo, imageUrl) {
      if (!imageUrl) return;
      const mediaImage = productInfo.querySelector('.product__media-item.is-active img, .product__media-item img');
      if (mediaImage) {
        mediaImage.src = imageUrl;
        mediaImage.removeAttribute('srcset');
      }

      const thumbImage = productInfo.querySelector('.thumbnail[aria-current="true"] img, .thumbnail img');
      if (thumbImage) {
        thumbImage.src = imageUrl;
        thumbImage.removeAttribute('srcset');
      }

      const gtMain = productInfo.querySelector('[data-clc-gt-main-img]');
      const gtThumb0 = productInfo.querySelector('[data-clc-gt-thumb0]');
      if (gtMain) {
        gtMain.src = imageUrl;
        gtMain.removeAttribute('srcset');
      }
      if (gtThumb0) {
        gtThumb0.src = imageUrl;
        gtThumb0.removeAttribute('srcset');
      }

      const gtWrap = productInfo.querySelector('.clc-gallery-thumbs');
      if (gtWrap) {
        gtWrap.querySelectorAll('.clc-gallery-thumbs__thumb').forEach((btn, i) => {
          btn.classList.toggle('is-active', i === 0);
          btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
        });
        const main = gtWrap.querySelector('.clc-gallery-thumbs__main');
        if (main) main.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }

    onClick = (event) => {
      const trigger = event.target.closest('.clc-variant-dropdown__trigger');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        this.toggleDropdown();
        return;
      }

      const button = event.target.closest('[data-variant-id]');
      if (!button || button.dataset.soldOut === 'true' || button.disabled) return;

      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      this.setActiveButton(button);
      this.updateAddToCartVariant(productInfo, button.dataset.variantId);
      this.updateGalleryImage(productInfo, button.dataset.imageUrl);

      if (button.dataset.productId) {
        productInfo.dataset.productId = button.dataset.productId;
      }
      this.syncAddToCartState(productInfo);
      clcEmitContextChanged(productInfo);
    };

    setActiveButton(activeButton) {
      this.querySelectorAll('.clc-option-button').forEach((button) => {
        const on = button === activeButton;
        button.classList.toggle('is-active', on);
        if (button.hasAttribute('aria-selected')) {
          button.setAttribute('aria-selected', on ? 'true' : 'false');
        }
      });
      const label = this.querySelector('.clc-variant-dropdown__trigger-label');
      const triggerBadge = this.querySelector('.clc-variant-dropdown__trigger-badge');
      if (label && activeButton && !activeButton.classList.contains('clc-variant-dropdown__trigger')) {
        const optionLabel = activeButton.querySelector('.clc-variant-dropdown__option-label');
        const optionBadge = activeButton.querySelector('.clc-variant-badge--inline-select');
        label.textContent = optionLabel ? optionLabel.textContent.trim() : activeButton.textContent.trim();
        if (triggerBadge) {
          const badgeText = optionBadge ? optionBadge.textContent.trim() : '';
          if (badgeText) {
            triggerBadge.textContent = badgeText;
            triggerBadge.hidden = false;
          } else {
            triggerBadge.textContent = '';
            triggerBadge.hidden = true;
          }
        }
      }
      this.closeDropdown();
    }
  }

  class ClcCustomQuantityOption extends HTMLElement {
    connectedCallback() {
      const productInfo = this.closest('product-info');
      const button = this.querySelector('.clc-option-button');
      if (!productInfo || !button) return;
      clcEnsureFormRcBundleOnSubmit(productInfo);
      this.initializeBundleSubmitHandler(productInfo);

      button.addEventListener('click', () => {
        productInfo
          .querySelectorAll('clc-custom-quantity-option .clc-option-button')
          .forEach((qtyButton) => qtyButton.classList.remove('is-active'));
        button.classList.add('is-active');
        this.applyQuantity(productInfo, button.dataset.quantity);
        clcEmitContextChanged(productInfo);
      });

      if (button.classList.contains('is-active')) {
        this.applyQuantity(productInfo, button.dataset.quantity);
      }
      clcEmitContextChanged(productInfo);
    }

    initializeBundleSubmitHandler(productInfo) {
      if (productInfo.dataset.clcBundleSubmitReady === 'true') return;
      const form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;

      productInfo.dataset.clcBundleSubmitReady = 'true';
      form.addEventListener(
        'submit',
        async (event) => {
        if (
          !productInfo.querySelector('.clc-custom-quantity-wrap') &&
          !productInfo.querySelector('clc-consumption-buy-box')
        ) {
          return;
        }

        const activeQtyButton = productInfo.querySelector(
          'clc-custom-quantity-option .clc-option-button.is-active'
        );
        const bundleSource = activeQtyButton?.closest('clc-custom-quantity-option');
        if (!bundleSource) return;
        const isConsumptionBundle = Boolean(
          bundleSource.closest('clc-consumption-buy-box')
        );

        const varietyPackProductId = bundleSource.dataset.varietyPackProductId;
        const activeFlavorBtn = productInfo.querySelector(
          'clc-custom-variants .clc-option-button.is-active[data-product-id]'
        );
        const flavorProductId =
          activeFlavorBtn?.dataset.productId || productInfo.dataset.productId;
        const flavorVariantId = activeFlavorBtn?.dataset.variantId;
        const isVarietyBundle =
          Boolean(varietyPackProductId) &&
          Boolean(flavorProductId) &&
          String(flavorProductId) === String(varietyPackProductId);
        const rcCfg = clcReadRcBundleConfig(productInfo);
        const selectedQty = Number(
          activeQtyButton?.dataset.quantity || bundleSource.dataset.quantity || 0
        );
        // Single flavor → one Recharge selection × Custom quantity (bundle discount).
        const isSingleFlavorBundle =
          !isConsumptionBundle &&
          !isVarietyBundle &&
          Boolean(rcCfg?.productId && rcCfg?.variantId) &&
          Boolean(flavorProductId) &&
          Boolean(flavorVariantId) &&
          selectedQty > 0;

        if (!isConsumptionBundle && !isVarietyBundle && !isSingleFlavorBundle) {
          return;
        }

        const bundleItems = [];
        if (isVarietyBundle) {
          for (let i = 1; i <= 5; i += 1) {
            const productId = bundleSource.getAttribute(`data-bundle-item-${i}-product-id`);
            const variantId = bundleSource.getAttribute(`data-bundle-item-${i}-variant-id`);
            const qty = Number(bundleSource.getAttribute(`data-bundle-item-${i}-qty`) || 0);
            if (variantId && qty > 0) {
              bundleItems.push({ id: Number(variantId), productId, qty });
            }
          }
        } else if (isConsumptionBundle || isSingleFlavorBundle) {
          const qty = isSingleFlavorBundle
            ? selectedQty
            : Number(activeQtyButton?.dataset.quantity || bundleSource.dataset.quantity || 0);
          if (!flavorProductId || !flavorVariantId || qty <= 0) return;
          bundleItems.push({
            id: Number(flavorVariantId),
            productId: String(flavorProductId),
            qty,
          });
        }
        if (!bundleItems.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const productFormEl = form.closest('product-form');
        const checkoutRedirect = productFormEl?.dataset.checkoutRedirect === 'true';
        const submitBtn = productFormEl?.querySelector('[type="submit"]');
        const spinner = productFormEl?.querySelector('.loading__spinner');

        // Show loading immediately so a cold Recharge first-click does not look like a no-op.
        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
        }
        spinner?.classList.remove('hidden');

        const sectionId = productInfo.dataset.section;
        const pricing = clcParseSubscriptionPricing(sectionId);

        const rcProps = rcCfg ? clcBuildRcBundleProperties(rcCfg) : null;

        let items = bundleItems.map((item) => {
          const line = {
            id: Number(item.id),
            quantity: Math.max(1, item.qty),
          };
          if (pricing) {
            const planId = clcPlanIdForVariant(pricing, String(item.id), 0);
            if (planId != null) line.selling_plan = planId;
          }
          if (rcProps) {
            line.properties = { ...rcProps };
          }
          return line;
        });

        if (
          isConsumptionBundle ||
          isSingleFlavorBundle ||
          (isVarietyBundle && rcCfg?.productId && rcCfg?.variantId)
        ) {
          try {
            items = await clcBuildRechargeBundleItems(bundleItems, rcCfg, pricing);
          } catch (error) {
            console.error('[clc-bundle-submit]', error);
            window.alert(error.message || 'Unable to create the discounted bundle. Please try again.');
            submitBtn?.classList.remove('loading');
            spinner?.classList.add('hidden');
            submitBtn?.removeAttribute('aria-disabled');
            return;
          }
        }

        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = { items };
        if (
          !checkoutRedirect &&
          cart &&
          typeof cart.getSectionsToRender === 'function'
        ) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        try {
          if (isConsumptionBundle) {
            const root = window.Shopify?.routes?.root || '/';
            const clearUrl = `${root}cart/clear.js`;
            const clearResponse = await fetch(clearUrl, {
              method: 'POST',
              headers: { Accept: 'application/json' },
            });
            if (!clearResponse.ok) {
              throw new Error('Unable to clear the cart before adding the new bundle.');
            }
          } else {
            await clcEnsureCartSession();
          }

          if (checkoutRedirect) {
            const startMarker =
              typeof window.CartPerformance?.createStartingMarker === 'function'
                ? window.CartPerformance.createStartingMarker('add:wait-for-subscribers')
                : null;

            // Verify pouches via /cart.js (add OK alone is not enough on first visit).
            let addResult = await clcAddPouchesAndVerify(payload, items, {
              retries: 2,
              waitAttempts: 12,
            });
            if (!addResult.ok) {
              window.alert(
                addResult.reason === 'gifts-only'
                  ? 'Only free gifts were added. Please try Checkout again.'
                  : addResult.message ||
                      'The pouches were not added to the cart. Please try again.'
              );
              return;
            }

            await clcPublishCartUpdate({
              source: 'product-form',
              productVariantId: String(items[0].id),
              cartData: addResult.parsedState,
            });
            if (startMarker && typeof window.CartPerformance?.measureFromMarker === 'function') {
              window.CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }

            // After gift apps run: pouches must still be present. Retry once if wiped.
            let ready = await clcWaitForCheckoutReady(addResult.pouchIds, 12);
            if (!ready.ok) {
              addResult = await clcAddPouchesAndVerify(payload, items, {
                retries: 1,
                waitAttempts: 12,
              });
              if (!addResult.ok) {
                window.alert(
                  addResult.reason === 'gifts-only'
                    ? 'Only free gifts were added. Please try Checkout again.'
                    : 'The pouches were not added to the cart. Please try again.'
                );
                return;
              }
              ready = addResult;
            }

            window.location.href = window.routes?.checkout_url || '/checkout';
            return;
          }

          const posted = await clcPostCartAddJson(payload);
          if (!posted.ok) {
            window.alert(
              posted.parsedState?.description ||
                posted.parsedState?.message ||
                'Unable to add the bundle. Please try again.'
            );
            return;
          }
          const parsedState = posted.parsedState;

          if (cart) {
            if (typeof cart.setActiveElement === 'function') {
              cart.setActiveElement(document.activeElement);
            }
            const startMarker =
              typeof window.CartPerformance?.createStartingMarker === 'function'
                ? window.CartPerformance.createStartingMarker('add:wait-for-subscribers')
                : null;
            await clcPublishCartUpdate({
              source: 'product-form',
              productVariantId: String(items[0].id),
              cartData: parsedState,
            });
            if (startMarker && typeof window.CartPerformance?.measureFromMarker === 'function') {
              window.CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }
            if (typeof window.CartPerformance?.measure === 'function') {
              window.CartPerformance.measure('add:paint-updated-sections', () => {
                cart.renderContents(parsedState);
              });
            } else {
              cart.renderContents(parsedState);
            }
            if (cart.classList.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          console.error('[clc-bundle-submit]', error);
          window.alert(error.message || 'Unable to add the bundle. Please try again.');
        } finally {
          submitBtn?.classList.remove('loading');
          spinner?.classList.add('hidden');
          submitBtn?.removeAttribute('aria-disabled');
        }
        },
        true
      );
    }

    applyQuantity(productInfo, quantity) {
      const form = productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;

      let quantityInput = form.querySelector('input[name="quantity"]');
      if (!quantityInput) {
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        form.appendChild(quantityInput);
      }

      quantityInput.value = Number(quantity) > 0 ? quantity : '1';
    }
  }

  class ClcSubscriptionWidget extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.sectionId = this.dataset.sectionId;
      this.boundUpdate = () => this.updateDisplay();
      this.pricing = null;
      this.productInfo?.addEventListener('clc:context-changed', this.boundUpdate);
      this.productInfo?.addEventListener('change', this.boundUpdate);
      this.loadPricing();
      clcEnsureFormRcBundleOnSubmit(this.productInfo);
      this.updateDisplay();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('clc:context-changed', this.boundUpdate);
      this.productInfo?.removeEventListener('change', this.boundUpdate);
    }

    loadPricing() {
      const el = document.getElementById(`ClcSubscriptionPricing-${this.sectionId}`);
      const fallbackFormat = this.dataset.moneyFormat || '';
      if (!el || !el.textContent.trim()) {
        this.pricing = { variants: {}, moneyFormat: fallbackFormat };
        return;
      }
      try {
        this.pricing = JSON.parse(el.textContent);
      } catch {
        this.pricing = { variants: {}, moneyFormat: fallbackFormat };
      }
      if (!this.pricing.moneyFormat && fallbackFormat) {
        this.pricing.moneyFormat = fallbackFormat;
      }
    }

    getVariantRow(variantId) {
      if (!variantId || !this.pricing?.variants) return null;
      return this.pricing.variants[String(variantId)];
    }

    updateDisplay() {
      const priceEl = this.querySelector('[data-clc-sub-price]');
      const cmpEl = this.querySelector('[data-clc-sub-compare]');
      const badgeEl = this.querySelector('[data-clc-sub-badge]');
      if (!priceEl || !this.productInfo) return;

      const pi = this.productInfo;
      const flavorBtn = pi.querySelector('clc-custom-variants .clc-option-button.is-active[data-variant-id]');
      const activeVariantId = flavorBtn?.dataset.variantId;
      const flavorProductId = flavorBtn?.dataset.productId || pi.dataset.productId;
      const qtyBtn = pi.querySelector('clc-custom-quantity-option .clc-option-button.is-active');
      const qtySource = qtyBtn?.closest('clc-custom-quantity-option');
      const varietyPackProductId = qtySource?.dataset?.varietyPackProductId;
      const fmt = this.pricing.moneyFormat || '';
      const idx = 0;
      const discountPct = clcParseSubscriptionDiscountPercent(qtySource);

      const isVarietyBundle =
        varietyPackProductId &&
        flavorProductId &&
        String(flavorProductId) === String(varietyPackProductId) &&
        qtySource;

      let mainCents = 0;
      let strikeCents = 0;
      let usedSubscriptionAllocation = false;

      if (isVarietyBundle) {
        const lines = [];
        for (let i = 1; i <= 5; i += 1) {
          const vid = qtySource.getAttribute(`data-bundle-item-${i}-variant-id`);
          const q = Number(qtySource.getAttribute(`data-bundle-item-${i}-qty`) || 0);
          if (!vid || q <= 0) continue;
          const row = this.getVariantRow(vid);
          if (!row) continue;
          const alloc = Array.isArray(row.allocations) ? row.allocations[idx] : null;
          lines.push({ row, q, alloc });
        }
        const everyAlloc = lines.length > 0 && lines.every((l) => l.alloc);
        if (everyAlloc) {
          usedSubscriptionAllocation = true;
          lines.forEach(({ alloc, q }) => {
            mainCents += alloc.price * q;
            const sc = alloc.compareAt && alloc.compareAt > 0 ? alloc.compareAt : alloc.price;
            strikeCents += sc * q;
          });
        } else {
          lines.forEach(({ row, q }) => {
            mainCents += row.price * q;
            const base = row.compareAt && row.compareAt > 0 ? row.compareAt : row.price;
            strikeCents += base * q;
          });
        }
      } else {
        const formEl = pi.querySelector('form[data-type="add-to-cart-form"]');
        const vid = activeVariantId || formEl?.querySelector('input[name="id"]')?.value;
        if (vid) {
          const activeQtyBtn = pi.querySelector('clc-custom-quantity-option .clc-option-button.is-active');
          const qtyWrap = activeQtyBtn?.closest('clc-custom-quantity-option');
          const qtyRaw = Number(
            activeQtyBtn?.dataset?.quantity ||
              qtyWrap?.dataset?.quantity ||
              formEl?.querySelector('input[name="quantity"]')?.value ||
              1
          );
          const lineQty = Math.max(1, Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1);

          const row = this.getVariantRow(vid);
          if (row) {
            const alloc = Array.isArray(row.allocations) ? row.allocations[idx] : null;
            if (alloc) {
              usedSubscriptionAllocation = true;
              const unitMain = alloc.price;
              let unitStrike = 0;
              if (alloc.compareAt && alloc.compareAt > alloc.price) {
                unitStrike = alloc.compareAt;
              } else if (row.compareAt && row.compareAt > row.price) {
                unitStrike = row.compareAt;
              }
              mainCents = unitMain * lineQty;
              strikeCents = unitStrike > unitMain ? unitStrike * lineQty : 0;
            } else {
              mainCents = row.price * lineQty;
              strikeCents =
                row.compareAt && row.compareAt > row.price ? row.compareAt * lineQty : 0;
            }
          }
        }
      }

      if (usedSubscriptionAllocation && discountPct > 0 && mainCents > 0) {
        const baseSub = mainCents;
        mainCents = Math.round((baseSub * (100 - discountPct)) / 100);
        strikeCents = Math.max(strikeCents || 0, baseSub);
      }

      priceEl.textContent = mainCents > 0 ? clcFormatMoneyCents(mainCents, fmt) : '—';
      if (cmpEl) {
        if (strikeCents > mainCents && mainCents > 0) {
          cmpEl.textContent = clcFormatMoneyCents(strikeCents, fmt);
          cmpEl.hidden = false;
        } else {
          cmpEl.textContent = '';
          cmpEl.hidden = true;
        }
      }
      if (badgeEl) {
        if (strikeCents > mainCents && mainCents > 0) {
          const pct = Math.round((1 - mainCents / strikeCents) * 100);
          if (pct > 0 && pct < 100) {
            badgeEl.textContent = `${pct}% OFF`;
            badgeEl.hidden = false;
          } else {
            badgeEl.hidden = true;
          }
        } else {
          badgeEl.hidden = true;
        }
      }

      clcSyncFormSellingPlan(this.productInfo, this.pricing);
    }
  }

  const CLC_US_STATE_CODES = {
    alabama: 'AL',
    alaska: 'AK',
    arizona: 'AZ',
    arkansas: 'AR',
    california: 'CA',
    colorado: 'CO',
    connecticut: 'CT',
    delaware: 'DE',
    florida: 'FL',
    georgia: 'GA',
    hawaii: 'HI',
    idaho: 'ID',
    illinois: 'IL',
    indiana: 'IN',
    iowa: 'IA',
    kansas: 'KS',
    kentucky: 'KY',
    louisiana: 'LA',
    maine: 'ME',
    maryland: 'MD',
    massachusetts: 'MA',
    michigan: 'MI',
    minnesota: 'MN',
    mississippi: 'MS',
    missouri: 'MO',
    montana: 'MT',
    nebraska: 'NE',
    nevada: 'NV',
    'new hampshire': 'NH',
    'new jersey': 'NJ',
    'new mexico': 'NM',
    'new york': 'NY',
    'north carolina': 'NC',
    'north dakota': 'ND',
    ohio: 'OH',
    oklahoma: 'OK',
    oregon: 'OR',
    pennsylvania: 'PA',
    'rhode island': 'RI',
    'south carolina': 'SC',
    'south dakota': 'SD',
    tennessee: 'TN',
    texas: 'TX',
    utah: 'UT',
    vermont: 'VT',
    virginia: 'VA',
    washington: 'WA',
    'west virginia': 'WV',
    wisconsin: 'WI',
    wyoming: 'WY',
    'district of columbia': 'DC',
  };

  function clcFormatShipsByDate(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).formatToParts(date);
    const weekday = parts.find((p) => p.type === 'weekday')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    return `${weekday}, ${month} ${day}`.toUpperCase();
  }

  function clcNormalizeStateCode(stateRaw, countryCode) {
    if (!stateRaw) return '';
    const trimmed = String(stateRaw).trim();
    if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
    if (countryCode && String(countryCode).toUpperCase() !== 'US') return trimmed.toUpperCase();
    return CLC_US_STATE_CODES[trimmed.toLowerCase()] || trimmed.toUpperCase();
  }

  function clcGetBrowserPosition(timeoutMs) {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('geolocation unavailable'));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: timeoutMs,
        maximumAge: 900000,
      });
    });
  }

  async function clcReverseGeocode(lat, lng) {
    const url = new URL('https://nominatim.openstreetmap.org/reverse');
    url.searchParams.set('lat', String(lat));
    url.searchParams.set('lon', String(lng));
    url.searchParams.set('format', 'json');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('zoom', '10');

    const response = await fetch(url.toString(), {
      headers: {
        Accept: 'application/json',
        'Accept-Language': 'en',
      },
    });
    if (!response.ok) throw new Error('reverse geocode failed');
    const data = await response.json();
    const address = data.address || {};
    const city =
      address.city ||
      address.town ||
      address.village ||
      address.hamlet ||
      address.municipality ||
      address.county ||
      '';
    const isoRegion = address['ISO3166-2-lvl4'] || '';
    const stateFromIso = isoRegion.includes('-') ? isoRegion.split('-').pop() : '';
    const stateCode = clcNormalizeStateCode(stateFromIso || address.state, address.country_code);
    const stateName = address.state || '';
    return { city, stateCode, stateName, countryCode: address.country_code || '' };
  }

  async function clcFetchIpLocation() {
    try {
      const response = await fetch('https://ipwho.is/');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return {
            city: data.city || '',
            stateCode: data.region_code || '',
            stateName: data.region || '',
            countryCode: data.country_code || '',
          };
        }
      }
    } catch (_err) {
      /* try backup */
    }

    const response = await fetch('https://api.country.is/?fields=city,subdivision');
    if (!response.ok) throw new Error('ip lookup failed');
    const data = await response.json();
    return {
      city: data.city || '',
      stateCode: data.subdivision || '',
      stateName: data.subdivision || '',
      countryCode: data.country || '',
    };
  }

  function clcFormatShippingLocation(loc, format, fallback) {
    const city = (loc.city || '').trim();
    const stateCode = clcNormalizeStateCode(loc.stateCode, loc.countryCode);
    const stateName = (loc.stateName || '').trim();
    const country = String(loc.countryCode || '').trim().toUpperCase();

    if (format === 'state' && stateName) return stateName.toUpperCase();
    if (format === 'state' && stateCode) return stateCode.toUpperCase();
    if (country && country !== 'US') {
      if (city) return `${city.toUpperCase()}, ${country}`;
      if (stateCode) return `${stateCode.toUpperCase()}, ${country}`;
      return country;
    }
    if (city && stateCode) return `${city.toUpperCase()}, ${stateCode}`;
    if (city) return city.toUpperCase();
    if (stateCode) return stateCode;
    return (fallback || 'your area').toUpperCase();
  }

  function clcUpdateShippingFlag(flagEl, countryCode) {
    if (!flagEl) return;
    const code = String(countryCode || '').trim().toLowerCase();
    if (!/^[a-z]{2}$/.test(code)) {
      flagEl.hidden = true;
      flagEl.innerHTML = '';
      return;
    }
    flagEl.hidden = false;
    flagEl.innerHTML = `<img class="clc-shipping-trust-bar__flag-image" src="https://flagcdn.com/w40/${code}.png" srcset="https://flagcdn.com/w80/${code}.png 2x" alt="" width="25" height="16" loading="lazy" decoding="async">`;
  }

  async function clcResolveShippingLocation(format, fallback) {
    const fallbackResult = {
      text: clcFormatShippingLocation({}, format, fallback),
      countryCode: '',
    };

    try {
      const ipLoc = await clcFetchIpLocation();
      const text = clcFormatShippingLocation(ipLoc, format, '');
      if (text && text !== (fallback || 'your area').toUpperCase()) {
        return { text, countryCode: ipLoc.countryCode || '' };
      }
      if (text) return { text, countryCode: ipLoc.countryCode || '' };
    } catch (_err) {
      /* try browser geolocation */
    }

    try {
      const position = await clcGetBrowserPosition(8000);
      const geoLoc = await clcReverseGeocode(position.coords.latitude, position.coords.longitude);
      const text = clcFormatShippingLocation(geoLoc, format, '');
      if (text) return { text, countryCode: geoLoc.countryCode || '' };
    } catch (_err) {
      /* use text fallback */
    }

    return fallbackResult;
  }

  class ClcSubscriptionBenefits extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.refillEl = this.querySelector('[data-clc-sub-benefit-refill]');
      if (!this.refillEl) return;

      this.text4 = this.dataset.refillText4 || 'Refills arrive every 4 weeks';
      this.text8 = this.dataset.refillText8 || 'Refills arrive every 8 weeks';
      this.qty8Weeks = Number(this.dataset.refillQty8Weeks || 15);

      this.boundUpdate = () => this.updateRefillLine();
      this.productInfo?.addEventListener('clc:context-changed', this.boundUpdate);
      this.updateRefillLine();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('clc:context-changed', this.boundUpdate);
    }

    updateRefillLine() {
      if (!this.refillEl || !this.productInfo) return;

      const qtyBtn = this.productInfo.querySelector(
        'clc-custom-quantity-option .clc-option-button.is-active'
      );
      const quantity = Number(qtyBtn?.dataset.quantity || 0);
      this.refillEl.textContent =
        quantity === this.qty8Weeks ? this.text8 : this.text4;
    }
  }

  class ClcShippingTrustBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.clcStbReady === 'true') return;
      this.dataset.clcStbReady = 'true';
      this.init();
    }

    async init() {
      const shipsByEl = this.querySelector('[data-clc-stb-ships-by]');
      const shippingToEl = this.querySelector('[data-clc-stb-shipping-to]');
      const flagEl = this.querySelector('[data-clc-stb-flag]');
      if (!shipsByEl || !shippingToEl) return;

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      shipsByEl.textContent = clcFormatShipsByDate(tomorrow);

      const fallback = this.dataset.locationFallback || 'your area';
      const format = this.dataset.locationFormat || 'city_state';
      shippingToEl.textContent = (fallback || 'your area').toUpperCase();
      clcUpdateShippingFlag(flagEl, '');

      try {
        const location = await clcResolveShippingLocation(format, fallback);
        shippingToEl.textContent = location.text;
        clcUpdateShippingFlag(flagEl, location.countryCode);
      } catch (_err) {
        shippingToEl.textContent = (fallback || 'your area').toUpperCase();
        clcUpdateShippingFlag(flagEl, '');
      }
    }
  }

  class ClcConsumptionBuyBox extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo) return;

      this.uiMode = this.dataset.uiMode || 'buttons';
      this.cycle = Number(this.dataset.cycle) || 28;
      this.cycle8 = Number(this.dataset.cycle8) || 56;
      this.qty8 = Number(this.dataset.refillQty8) || 15;
      this.pouchesPerCan = Number(this.dataset.pouchesPerCan) || 20;
      this.sliderMin = Number(this.dataset.sliderMin) || 2;
      this.sliderMax = Number(this.dataset.sliderMax) || 10;
      this.sectionId = this.dataset.sectionId || this.productInfo.dataset.section;
      this.rateUnit = this.dataset.rateUnit || 'a day';
      this.slider = this.querySelector('.clc-consumption-buy-box__slider');

      clcEnsureFormRcBundleOnSubmit(this.productInfo);
      const firstQtyOption = this.querySelector('clc-custom-quantity-option');
      if (firstQtyOption) {
        ClcCustomQuantityOption.prototype.initializeBundleSubmitHandler.call(
          firstQtyOption,
          this.productInfo
        );
      }

      if (this.uiMode === 'slider' && this.slider) {
        this.slider.addEventListener('input', () => this.paint());
      } else {
        this.querySelectorAll('.clc-consumption-buy-box__rate').forEach((rateEl) => {
          rateEl.addEventListener('click', () => {
            this.querySelectorAll('.clc-consumption-buy-box__rate').forEach((el) => {
              el.classList.remove('is-active');
            });
            rateEl.classList.add('is-active');
            const input = rateEl.querySelector('input[type="radio"]');
            if (input) input.checked = true;
            this.paint();
          });
        });
      }

      this.paint();
    }

    getRate() {
      if (this.uiMode === 'slider' && this.slider) {
        return Number(this.slider.value) || Number(this.dataset.defaultRate) || 3;
      }
      const active = this.querySelector('.clc-consumption-buy-box__rate.is-active');
      return Number(active?.dataset.rate) || Number(this.dataset.defaultRate) || 3;
    }

    getRateLabel() {
      if (this.uiMode === 'slider') return `${this.getRateShort()} ${this.rateUnit}`;
      const active = this.querySelector('.clc-consumption-buy-box__rate.is-active');
      const label = active?.dataset.rateLabel || String(this.getRate());
      return `${label} a day`;
    }

    getRateShort() {
      if (this.uiMode === 'slider') {
        const rate = this.getRate();
        return rate >= this.sliderMax ? `${this.sliderMax}+` : String(rate);
      }
      const active = this.querySelector('.clc-consumption-buy-box__rate.is-active');
      return active?.dataset.rateLabel || String(this.getRate());
    }

    cycleForTier(tier) {
      const quantity = Number(tier?.dataset.quantity) || Number(tier?.dataset.cans) || 0;
      return quantity >= this.qty8 ? this.cycle8 : this.cycle;
    }

    activateTierSource(tierEl) {
      const tierKey = tierEl?.dataset.tier;
      if (!tierKey) return;

      const source = this.querySelector(`clc-custom-quantity-option[data-consumption-tier="${tierKey}"]`);
      const button = source?.querySelector('.clc-option-button');
      if (!button) return;

      this.querySelectorAll('clc-custom-quantity-option .clc-option-button').forEach((btn) => {
        btn.classList.remove('is-active');
      });
      button.classList.add('is-active');

      const form = this.productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (form) {
        let quantityInput = form.querySelector('input[name="quantity"]');
        if (!quantityInput) {
          quantityInput = document.createElement('input');
          quantityInput.type = 'hidden';
          quantityInput.name = 'quantity';
          form.appendChild(quantityInput);
        }
        const qty = Number(button.dataset.quantity) || Number(source.dataset.quantity) || 1;
        quantityInput.value = qty > 0 ? String(qty) : '1';
      }

      const pricing = clcParseSubscriptionPricing(this.sectionId);
      clcSyncFormSellingPlan(this.productInfo, pricing);
      clcEmitContextChanged(this.productInfo);
    }

    paintSliderChrome(rate, rateShort) {
      const readout = this.querySelector('[data-clc-cb-readout]');
      if (readout) readout.textContent = rateShort;

      const zone = rate <= 3 ? 1 : rate <= 7 ? 2 : 3;
      const ticks = {
        1: this.dataset.tickLow || 'Low · 2',
        2: this.dataset.tickMid || 'Regular',
        3: this.dataset.tickHigh || 'All-day',
      };
      this.querySelectorAll('[data-clc-cb-tick]').forEach((el) => {
        const n = Number(el.dataset.clcCbTick);
        const label = ticks[n] || el.textContent;
        el.innerHTML = n === zone ? `<b>${label}</b>` : label;
      });
    }

    paint() {
      const rate = this.getRate();
      const rateLabel = this.getRateLabel();
      const rateShort = this.getRateShort();
      const monthly = Math.round(rate * this.cycle);

      this.querySelectorAll('[data-clc-cb-rate-label]').forEach((el) => {
        el.textContent = rateLabel;
      });
      this.querySelectorAll('[data-clc-cb-monthly]').forEach((el) => {
        el.textContent = String(monthly);
      });
      this.querySelectorAll('[data-clc-cb-rate-short]').forEach((el) => {
        el.textContent = rateShort;
      });

      if (this.uiMode === 'slider') this.paintSliderChrome(rate, rateShort);

      const tiers = [...this.querySelectorAll('.clc-consumption-buy-box__tier')];
      let best = null;

      tiers.forEach((tier) => {
        const cans = Number(tier.dataset.cans) || 0;
        const pouches = Number(tier.dataset.pouches) || cans * this.pouchesPerCan;
        const days = rate > 0 ? Math.round(pouches / rate) : 0;
        const cycle = this.cycleForTier(tier);
        const daysEl = tier.querySelector('[data-clc-cb-days]');
        if (daysEl) daysEl.textContent = `~${days} days`;
        const covers = this.uiMode === 'slider' ? pouches / rate >= cycle : days >= cycle;
        if (covers && !best) best = tier;
      });

      if (!best && tiers.length) best = tiers[tiers.length - 1];

      tiers.forEach((tier) => {
        const recommended = tier === best;
        tier.classList.toggle('is-recommended', recommended);
        if (!recommended || this.uiMode !== 'slider') {
          if (!recommended) tier.classList.remove('is-thin');
          return;
        }

        const cans = Number(tier.dataset.cans) || 0;
        const pouches = Number(tier.dataset.pouches) || cans * this.pouchesPerCan;
        const days = rate > 0 ? Math.round(pouches / rate) : 0;
        const cycle = this.cycleForTier(tier);
        const gap = cycle - days;
        const isShort = gap > 0;
        const refillEl = tier.querySelector('[data-clc-cb-refill]');
        if (refillEl) {
          refillEl.textContent =
            (Number(tier.dataset.quantity) || cans) >= this.qty8
              ? this.dataset.refillLabel8 || 'Refills every 8 weeks'
              : this.dataset.refillLabel4 || 'Refills every 4 weeks';
        }
        const shortEl = tier.querySelector('[data-clc-cb-short]');
        if (shortEl) {
          const template =
            this.dataset.shortWarning || 'Runs out about [days] day(s) before your next refill.';
          shortEl.textContent = template
            .replace('[days]', String(gap))
            .replace('day(s)', gap === 1 ? 'day' : 'days');
          shortEl.hidden = !isShort;
        }
        tier.classList.toggle('is-thin', isShort);
      });

      if (best) this.activateTierSource(best);
    }
  }

  if (!customElements.get('clc-consumption-buy-box')) {
    customElements.define('clc-consumption-buy-box', ClcConsumptionBuyBox);
  }

  if (!customElements.get('clc-custom-variants')) {
    customElements.define('clc-custom-variants', ClcCustomVariants);
  }
  if (!customElements.get('clc-custom-quantity-option')) {
    customElements.define('clc-custom-quantity-option', ClcCustomQuantityOption);
  }
  if (!customElements.get('clc-subscription-widget')) {
    customElements.define('clc-subscription-widget', ClcSubscriptionWidget);
  }
  if (!customElements.get('clc-subscription-benefits')) {
    customElements.define('clc-subscription-benefits', ClcSubscriptionBenefits);
  }
  if (!customElements.get('clc-shipping-trust-bar')) {
    customElements.define('clc-shipping-trust-bar', ClcShippingTrustBar);
  }

  function clcMurmurHash32(seed) {
    let s = seed >>> 0;
    s = Math.imul(s ^ (s >>> 16), 0x45d9f3b);
    s = Math.imul(s ^ (s >>> 16), 0x45d9f3b);
    return (s ^ (s >>> 16)) >>> 0;
  }

  function clcOrderCountBucketSeed(now) {
    return (
      now.getFullYear() * 10000 +
      (now.getMonth() + 1) * 100 +
      now.getDate() +
      now.getHours() * 60 +
      Math.floor(now.getMinutes() / 5) * 5
    );
  }

  function clcOrderCountHourCurve(hour, minute) {
    const t = hour + minute / 60;
    if (t < 6) return 0.08 + (t / 6) * 0.12;
    if (t < 10) return 0.2 + ((t - 6) / 4) * 0.35;
    if (t < 14) return 0.55 + ((t - 10) / 4) * 0.3;
    if (t < 17) return 0.85 + ((t - 14) / 3) * 0.15;
    if (t < 21) return 1 - ((t - 17) / 4) * 0.35;
    return 0.65 - ((t - 21) / 3) * 0.45;
  }

  function clcOrderCountWeekdayFactor(day) {
    if (day >= 2 && day <= 4) return 1.06;
    if (day === 0 || day === 6) return 0.93;
    return 1;
  }

  function clcOrderCountBand(hour, minute) {
    if (hour >= 10 && hour < 21) return [1500, 1920];
    if (hour < 8) return [1050, 1300];
    if (hour < 10) {
      const progress = (hour - 8 + minute / 60) / 2;
      return [Math.floor(1050 + progress * 450), Math.floor(1300 + progress * 620)];
    }
    const progress = (hour - 21 + minute / 60) / 3;
    return [Math.floor(1500 - progress * 450), Math.floor(1920 - progress * 620)];
  }

  function clcAvoidRoundOrderCount(value, min, max) {
    let count = value;
    if (count % 100 === 0) count += 7;
    else if (count % 50 === 0) count += 3;
    else if (count % 10 === 0) count += 1;
    if (count % 2 === 0) count += 1;
    return Math.max(min, Math.min(max, count));
  }

  function clcLiveOrderCount(now = new Date()) {
    const MIN = 1050;
    const MAX = 1920;
    const hour = now.getHours();
    const minute = now.getMinutes();
    const [bandMin, bandMax] = clcOrderCountBand(hour, minute);
    const bandSpan = Math.max(1, bandMax - bandMin);
    const curve = clcOrderCountHourCurve(hour, minute) * clcOrderCountWeekdayFactor(now.getDay());
    const base = bandMin + Math.floor(bandSpan * Math.min(1, Math.max(0, curve)));
    const hash = clcMurmurHash32(clcOrderCountBucketSeed(now));
    const drift = (hash % 11) - 7;
    const micro = ((hash >>> 8) % 5) - 2;
    return clcAvoidRoundOrderCount(base + drift + micro, MIN, MAX);
  }

  class ClcLiveOrderCounter extends HTMLElement {
    connectedCallback() {
      this.countEl = this.querySelector('[data-order-count]');
      this.render();
      this.interval = window.setInterval(() => this.render(), 300000);
    }

    disconnectedCallback() {
      if (this.interval) window.clearInterval(this.interval);
    }

    render() {
      if (!this.countEl) return;
      this.countEl.textContent = clcLiveOrderCount().toLocaleString();
    }
  }

  if (!customElements.get('clc-live-order-counter')) {
    customElements.define('clc-live-order-counter', ClcLiveOrderCounter);
  }
})();
